<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useApiClient } from "@/api/client";
import router from "@/router/router";
import { useCurveModelStore } from "@/modules/booking_experts/store";
import dayjs from "dayjs";
import Switch from "@/ui/components/switch/switch.vue";
type ComplexPriceItemDTO =
	Modules.BookingExperts.Data.MasterPriceList.ComplexPriceItemDTO;

const api = useApiClient("bookingExperts");

const id = Number(router.currentRoute.value.params.id);

const complexPriceItemsHistoryItems = ref([] as ComplexPriceItemDTO[]);
const isLoading = ref(false);
const total = ref(200);
const selectedItemsPerPage = ref(25);
const selectedPage = ref(1);

type HeaderType = {
	title: string;
	key: string;
	align?: "start" | "end";
	sortable?: boolean;
};

const headers: HeaderType[] = [
	{
		title: "Arrival Date",
		align: "start",
		key: "arrival_date",
		sortable: false,
	},
	{
		title: "Length of Stay (days)",
		align: "start",
		key: "length_of_stay",
		sortable: false,
	},
	{
		title: "Price",
		align: "start",
		key: "price_value",
		sortable: false,
	},
	{
		title: "Factor Applied",
		align: "start",
		key: "applied_factor",
		sortable: false,
	},
	{
		title: "Absolute Price Change Applied",
		align: "start",
		key: "applied_absolute_offset",
		sortable: false,
	},
	{
		title: "Valid From",
		align: "start",
		key: "valid_from",
		sortable: false,
	},
	{
		title: "Valid To",
		align: "start",
		key: "valid_to",
		sortable: false,
	},
];

// This var is used as input for the :search prop
// Modifying the search prop triggers a data update.
const loadActiveOnly = ref(true);

const loadPrices = async ({ page, itemsPerPage: per_page, sortBy }) => {
	isLoading.value = true;
	selectedItemsPerPage.value = per_page;
	selectedPage.value = page;

	const sort = sortBy[0]
		? (sortBy[0]["order"] === "desc" ? "-" : "") + sortBy[0]["key"]
		: undefined;

	per_page = per_page === -1 ? 1000 : per_page;

	const response = await api.getManagedPriceListPricingHistory(id, {
		page,
		per_page,
		sort,
		"filter[active_only]": loadActiveOnly.value,
	});
	if (response.isOk()) {
		complexPriceItemsHistoryItems.value = response.value.data;
		total.value = response.value.total;
	}

	isLoading.value = false;
};

onMounted(() => {
	useCurveModelStore().currentBreadcrumb = "History";
});

onUnmounted(() => {
	useCurveModelStore().currentBreadcrumb = null;
});
</script>

<template>
	<v-card class="pa-4 mt-5">
		<div class="d-flex flex-row justify-space-between mb-2 px-2">
			<h3>History</h3>
			<Switch
				label="Show active prices only"
				label-position="right"
				v-model="loadActiveOnly"
			/>
		</div>
		<!--					:headers="headers"-->
		<v-data-table-server
			:headers="headers"
			:items="complexPriceItemsHistoryItems"
			:items-per-page="selectedItemsPerPage"
			:page="selectedPage"
			:items-length="total"
			@update:options="loadPrices"
			:loading="isLoading"
			:search="loadActiveOnly"
		>
			<template v-slot:item.arrival_date="{ item }">
				{{ dayjs(item.arrival_date).format("YYYY-MM-DD") }}
			</template>
			<template v-slot:item.price_value="{ item }">
				{{ item.price_value }} {{ item.price_currency }}
			</template>
			<template v-slot:item.applied_factor="{ item }">
				<span v-if="item.applied_factor == null">-</span>
				<v-chip
					v-else
					density="compact"
					label
					:color="
						item.applied_factor > 1
							? 'success'
							: item.applied_factor < 1
							? 'error'
							: ''
					"
					>{{ item.applied_factor }}</v-chip
				>
			</template>
			<template v-slot:item.applied_absolute_offset="{ item }">
				<span v-if="item.applied_absolute_offset == null">-</span>
				<v-chip
					v-else
					density="compact"
					label
					:color="
						item.applied_absolute_offset > 0
							? 'success'
							: item.applied_absolute_offset < 0
							? 'error'
							: ''
					"
					>{{ item.applied_absolute_offset }}</v-chip
				>
			</template>
			<template v-slot:item.valid_from="{ item }">
				{{ dayjs(item.valid_from).format("YYYY-MM-DD HH:mm:ss") }}
			</template>
			<template v-slot:item.valid_to="{ item }">
				{{
					item.valid_to !== undefined
						? dayjs(item.valid_to).format("YYYY-MM-DD HH:mm:ss")
						: "Active"
				}}
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss"></style>
